import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, ShoppingCart, Loader2, XCircle, Trash2, RefreshCw, Smartphone, Banknote, ScanBarcode, Bluetooth, Usb, Camera, CreditCard, Plus, Minus, Phone
} from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { checkPaymentStatus, retryPayment, CheckoutRequest, Cart } from '@/api/cart';
import { usePOSCartStore } from '@/stores/usePOSCartStore';
import { useOfflineSyncStore } from '@/stores/useOfflineSyncStore';
import { playSuccessSound, playErrorSound } from '@/utils/sound';
import apiClient from '@/services/apiClient';
import { extractImageUrls } from '@/config/media';

const calculateBackoffDelay = (attempt: number): number => {
  const baseDelay = 1000;
  const maxDelay = 10000;
  return Math.min(baseDelay * Math.pow(2, attempt - 1), maxDelay);
};

interface POSCartSidebarProps {
  businessId: string;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const POSCartSidebar: React.FC<POSCartSidebarProps> = ({ businessId, isCollapsed = false, onToggleCollapse }) => {
  const navigate = useNavigate();
  const toastCtx = useToast();

  const { cart, loading, error, updateQuantity, removeItem, loadCart, clearCart } = usePOSCartStore();
  const subtotal = cart?.total_amount || 0;

  const showToast = (opts: { type: 'success' | 'error' | 'warning' | 'info'; message: string }) => {
    if (opts.type === 'success') toastCtx.success(opts.message);
    else if (opts.type === 'error') toastCtx.error('Error', opts.message);
    else if (opts.type === 'warning') toastCtx.warning('Warning', opts.message);
    else if (opts.type === 'info') toastCtx.info('Info', opts.message);
  };

  useEffect(() => {
    if (businessId) loadCart(businessId);
    return () => {
      stopPaymentStatusPolling();
      // Reset all transient UI states so navigating back doesn't leave stuck loading
      setProcessing(false);
      setCheckoutModalVisible(false);
      setPaymentStatusModalVisible(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessId]);

  // Checkout Modal State
  const [checkoutModalVisible, setCheckoutModalVisible] = useState(false);
  const [scannerModalVisible, setScannerModalVisible] = useState(false);
  const [mpesaSetupRequired, setMpesaSetupRequired] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<'cash' | 'mpesa'>('cash');
  const [customerPhone, setCustomerPhone] = useState('');
  const [discount, setDiscount] = useState('0');
  const [mpesaManualCode, setMpesaManualCode] = useState('');
  const [processing, setProcessing] = useState(false);

  // Payment Status Polling State
  const [paymentStatusModalVisible, setPaymentStatusModalVisible] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<'checking' | 'success' | 'failed'>('checking');
  const [currentSaleId, setCurrentSaleId] = useState<string | null>(null);
  const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
  const [preCheckoutCart, setPreCheckoutCart] = useState<Cart | null>(null);

  const pollingIntervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const checkCountRef = useRef(0);

  const handleUpdateQuantity = async (itemId: string, newQuantity: number) => {
    if (!businessId || newQuantity < 0) return;
    try {
      await updateQuantity(businessId, itemId, newQuantity);
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || 'Failed to update quantity' });
    }
  };

  const handleRemoveItem = async (itemId: string) => {
    if (!businessId) return;
    try {
      await removeItem(businessId, itemId);
    } catch (err: any) {
      showToast({ type: 'error', message: err.message || 'Failed to remove item' });
    }
  };

  const resetCheckoutForm = () => {
    setPaymentMethod('cash');
    setCustomerPhone('');
    setDiscount('0');
    setMpesaManualCode('');
    setMpesaSetupRequired(false);
  };

  const handleCheckoutSubmit = async () => {
    if (!businessId || processing) return;

    if (!cart?.cart_items?.length) {
      showToast({ type: 'warning', message: 'Cart is empty' });
      return;
    }

    const hasManualCode = mpesaManualCode.trim().length > 0;
    if (paymentMethod === 'mpesa' && !hasManualCode && !customerPhone) {
      showToast({ type: 'warning', message: 'Phone required for M-Pesa' });
      return;
    }

    const sanitizedPhone = customerPhone.replace(/\D/g, '');
    if (paymentMethod === 'mpesa' && !hasManualCode && sanitizedPhone.length < 9) {
      showToast({ type: 'warning', message: 'Valid phone required' });
      return;
    }

    setProcessing(true);
    setPreCheckoutCart(cart);

    try {
      if (paymentMethod === 'cash') {
        // OFFLINE-FIRST: Push directly to sync queue and complete instantly!
        useOfflineSyncStore.getState().addSaleToQueue({
          businessId,
          cart: cart as Cart,
          paymentMethod: 'cash',
          amount: subtotal,
          customerPhone: sanitizedPhone || undefined,
          notes: '',
        });
        
        showSuccessAlert({ receipt_number: 'SYNC-PENDING', amount: subtotal, total: subtotal });
        setPreCheckoutCart(null);
        return;
      }

        // M-PESA requires internet instantly for STK Push and uses the online checkout endpoint
        const checkoutPayload = {
          paymentMethod,
          customerPhone: sanitizedPhone,
          discount: parseFloat(discount) || 0,
          mpesaCode: hasManualCode ? mpesaManualCode.trim() : undefined,
          items: cart.cart_items.map(item => {
            const product = (item as any).product || (item as any).products;
            return {
              product_id: item.product_id,
              barcode: product?.barcode || product?.manufacturer_barcode || null,
              name: product?.name || 'Unknown Product',
              quantity: item.quantity,
              unit_price: product?.price || 0,
              total_price: (product?.price || 0) * item.quantity,
            };
          })
        };

        // API call to the online checkout endpoint for STK Push
        const { data: res } = await apiClient.post(`/cart/${businessId}/checkout`, checkoutPayload);

      if (paymentMethod === 'mpesa' && res.mpesa) {
        setCurrentSaleId(res.sale.id);
        setPaymentStatus('checking');
        setPaymentStatusModalVisible(true);
        setCheckoutModalVisible(false);
        checkCountRef.current = 0;
        startPaymentStatusPolling(res.sale.id);
      } else {
        showSuccessAlert(res.receipt || { receipt_number: 'PAID', amount: subtotal });
        setPreCheckoutCart(null);
      }
    } catch (error: any) {
      playErrorSound();
      console.error('Checkout error:', error);
      setProcessing(false);
      const msg = error.response?.data?.error || error.message || 'Checkout failed';
      
      // Detect if error is due to missing M-Pesa configuration
      const isConfigError = msg.toLowerCase().includes('not configured') || 
                            msg.toLowerCase().includes('setup') || 
                            msg.toLowerCase().includes('config') ||
                            msg.toLowerCase().includes('credentials');
                            
      if (paymentMethod === 'mpesa' && isConfigError) {
        setMpesaSetupRequired(true);
      } else {
        showToast({ type: 'error', message: msg });
      }
    } finally {
      setProcessing(false);
    }
  };

  const startPaymentStatusPolling = (saleId: string) => {
    stopPaymentStatusPolling();
    checkMpesaPaymentStatus(saleId);
  };

  const stopPaymentStatusPolling = () => {
    if (pollingIntervalRef.current) clearTimeout(pollingIntervalRef.current);
    if (pollingTimeoutRef.current) clearTimeout(pollingTimeoutRef.current);
    checkCountRef.current = 0;
  };

  const checkMpesaPaymentStatus = async (saleId: string) => {
    if (!businessId) return;
    try {
      checkCountRef.current += 1;
      const maxAttempts = 30; // ~2 mins

      if (checkCountRef.current > maxAttempts) {
        stopPaymentStatusPolling();
        setPaymentStatus('failed');
        setPaymentErrorMessage('Payment timeout. Customer may not have completed the payment.');
        playErrorSound();
        await restoreCartAfterFailure();
        return;
      }

      const res = await checkPaymentStatus(businessId, saleId);

      if (res.status === 'completed') {
        stopPaymentStatusPolling();
        setPaymentStatus('success');
        playSuccessSound();
        pollingTimeoutRef.current = setTimeout(() => {
          setPaymentStatusModalVisible(false);
          clearCart(); // Clear immediately
          loadCart(businessId);
          resetCheckoutForm();
          setPreCheckoutCart(null);
          showToast({ type: 'success', message: 'Sale Completed!' });
        }, 2000);
      } else if (res.status === 'failed') {
        stopPaymentStatusPolling();
        setPaymentStatus('failed');
        setPaymentErrorMessage(res.message || 'Payment failed');
        playErrorSound();
        await restoreCartAfterFailure();
      } else {
        const delay = calculateBackoffDelay(checkCountRef.current);
        pollingIntervalRef.current = setTimeout(() => checkMpesaPaymentStatus(saleId), delay);
      }
    } catch (error) {
      if (checkCountRef.current < 30) {
        const delay = calculateBackoffDelay(checkCountRef.current);
        pollingIntervalRef.current = setTimeout(() => checkMpesaPaymentStatus(saleId), delay);
      } else {
        stopPaymentStatusPolling();
        setPaymentStatus('failed');
        setPaymentErrorMessage('Unable to verify payment status. Check manually.');
        playErrorSound();
        await restoreCartAfterFailure();
      }
    }
  };

  const restoreCartAfterFailure = async () => {
    if (!preCheckoutCart?.cart_items?.length || !businessId) return;
    try {
      await loadCart(businessId);
    } catch (error) {
      console.error('Failed to restore cart', error);
    }
  };

  const handleRetryPayment = async () => {
    if (!businessId || !currentSaleId) return;
    try {
      await retryPayment(businessId, currentSaleId);
      setPaymentStatus('checking');
      checkCountRef.current = 0;
      startPaymentStatusPolling(currentSaleId);
      showToast({ type: 'info', message: 'Payment retry initiated' });
    } catch (error: any) {
      showToast({ type: 'error', message: error.response?.data?.error || 'Retry failed' });
    }
  };

  const showSuccessAlert = (receipt: any) => {
    playSuccessSound();
    const total = receipt?.total || receipt?.amount || subtotal;
    showToast({ type: 'success', message: `Sale Completed! Receipt: ${receipt?.receipt_number || 'PENDING'}, Total: KES ${total}` });
    setCheckoutModalVisible(false);
    clearCart(); // Clear immediately
    setProcessing(false);
    resetCheckoutForm();
  };

  return (
    <div className="flex flex-col h-full bg-white dark:bg-[#1E1E1E] shadow-xl overflow-hidden border-l border-light-border dark:border-dark-border transition-all duration-300">
        {isCollapsed ? (
          /* Collapsed State - Just icon bar */
          <div className="flex flex-col items-center h-full py-4 gap-4 w-16">
            {onToggleCollapse && (
              <button
                onClick={onToggleCollapse}
                className="p-2 rounded-lg bg-primary text-white hover:bg-primary/90 transition"
                title="Expand Cart"
              >
                <ShoppingCart className="h-5 w-5" />
              </button>
            )}
            <div className="flex-1 flex items-center">
              <div className="w-8 h-8 rounded-full bg-primary text-white font-bold text-xs flex items-center justify-center">
                {cart?.cart_items?.length || 0}
              </div>
            </div>
          </div>
        ) : (
          /* Expanded State - Full cart */
          <>
        {/* Hardware Status Banner */}
        <button 
          onClick={() => setScannerModalVisible(true)}
          className="w-full flex items-center justify-between px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border-b border-blue-100 dark:border-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/40 transition group"
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
            <span className="text-xs font-bold text-blue-700 dark:text-blue-400">Barcode Listening Mode</span>
          </div>
          <ScanBarcode className="h-4 w-4 text-blue-600 dark:text-blue-400 opacity-70 group-hover:opacity-100 transition" />
        </button>

        {/* Header */}
          <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-dark-card flex items-center justify-between">
            <div className="flex items-center flex-1">
              {onToggleCollapse && (
                <button
                  onClick={onToggleCollapse}
                  className="mr-3 p-1 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 lg:hidden"
                >
                  <XCircle className="h-4 w-4 text-gray-500" />
                </button>
              )}
              <ShoppingCart className="h-5 w-5 text-primary mr-2" />
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Current Sale</h2>
            </div>
            
            {cart?.cart_items && cart.cart_items.length > 0 && (
              <button
                onClick={() => {
                  if (window.confirm("Are you sure you want to clear the entire cart?")) {
                    clearCart();
                  }
                }}
                className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors flex items-center justify-center group ml-2"
                title="Clear entire cart"
              >
                <Trash2 size={18} className="group-hover:scale-110 transition-transform" />
              </button>
            )}
          </div>

      {/* Cart Items List */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50 dark:bg-[#121212]">
        {loading && !cart ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : !cart?.cart_items?.length ? (
          <div className="flex flex-col items-center justify-center h-full text-center py-12">
            <ShoppingCart className="h-12 w-12 text-gray-300 dark:text-gray-600 mb-3" />
            <h3 className="font-bold text-gray-900 dark:text-white">Cart is empty</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Scan or tap products to add them</p>
          </div>
        ) : (
          [...cart.cart_items]
            .sort((a: any, b: any) => {
              const timeA = new Date(a.created_at || 0).getTime();
              const timeB = new Date(b.created_at || 0).getTime();
              return timeB - timeA; // Descending (newest first)
            })
            .map((item: any) => {
            const product = item.products || item.product;
            if (!product) return null;
            return (
            <div key={item.id} className="bg-white dark:bg-[#1E1E1E] rounded-xl p-3 shadow-sm flex items-start gap-3 border border-light-border dark:border-dark-border">
              {(() => {
                const productImg = extractImageUrls(product.media_urls)[0] || product.image_url || null;
                return (
                  <div className="w-12 h-12 rounded-lg bg-gray-100 dark:bg-[#2A2A2A] flex-shrink-0 flex items-center justify-center overflow-hidden">
                    {productImg ? (
                      <img
                        src={productImg}
                        alt={product.name || 'Product'}
                        className="w-full h-full object-cover"
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    ) : (
                      <ShoppingCart className="h-5 w-5 text-gray-400" />
                    )}
                  </div>
                );
              })()}
              <div className="flex-1 min-w-0">
                <h4 className="font-bold text-sm text-gray-900 dark:text-white line-clamp-1">{product.name || 'Unknown Product'}</h4>
                <p className="text-xs font-semibold text-primary mt-0.5">KES {(product.price || 0).toLocaleString()}</p>
                <div className="flex items-center gap-2 mt-2">
                  <div className="flex items-center bg-gray-50 dark:bg-[#2A2A2A] rounded-md border border-gray-200 dark:border-gray-700">
                    <button 
                      onClick={() => handleUpdateQuantity(item.id, item.quantity - 1)}
                      className="w-7 h-7 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-danger"
                    >
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="font-bold text-sm text-gray-900 dark:text-white w-6 text-center">{item.quantity}</span>
                    <button 
                      onClick={() => handleUpdateQuantity(item.id, item.quantity + 1)}
                      className="w-7 h-7 flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-primary"
                    >
                      <Plus className="h-3 w-3" />
                    </button>
                  </div>
                  <button 
                    onClick={() => handleRemoveItem(item.id)}
                    className="p-1.5 text-gray-400 hover:text-danger rounded-md hover:bg-red-50 dark:hover:bg-red-900/30 transition"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="font-bold text-sm text-gray-900 dark:text-white text-right">
                KES {((product.price || 0) * item.quantity).toLocaleString()}
              </div>
            </div>
            );
          })
        )}
      </div>

      {/* Cart Summary */}
      <div className="bg-white dark:bg-[#1E1E1E] p-4 border-t border-light-border dark:border-dark-border">
        <div className="flex justify-between items-center mb-4">
          <span className="font-bold text-gray-500 dark:text-gray-400">Total</span>
          <span className="font-black text-primary text-xl">KES {subtotal.toLocaleString()}</span>
        </div>
        <button
          onClick={() => setCheckoutModalVisible(true)}
          disabled={!cart?.cart_items?.length}
          className="w-full py-3.5 bg-primary text-white font-bold rounded-xl hover:opacity-90 disabled:opacity-50 transition shadow-lg shadow-primary/30 text-lg flex justify-center items-center gap-2"
        >
          <CreditCard className="h-5 w-5" /> Checkout
        </button>
      </div>

      {/* Checkout Modal */}
      {checkoutModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-[#1E1E1E] w-full max-w-md rounded-3xl p-6 shadow-2xl relative">
            <button 
              onClick={() => setCheckoutModalVisible(false)}
              className="absolute top-4 right-4 p-2 bg-gray-100 dark:bg-[#2A2A2A] rounded-full hover:opacity-80 text-gray-500 dark:text-gray-400"
            >
              <XCircle className="h-6 w-6" />
            </button>
            
            <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">Complete Sale</h2>
            
            {mpesaSetupRequired ? (
              <div className="flex flex-col items-center justify-center py-6 text-center">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 text-red-500 rounded-full flex items-center justify-center mb-4">
                  <XCircle className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">M-Pesa Not Configured</h3>
                <p className="text-gray-500 dark:text-gray-400 mb-6 px-4 text-sm leading-relaxed">
                  You need to configure your M-Pesa Paybill or Till Number before you can accept mobile payments.
                </p>
                <button
                  onClick={() => {
                    setCheckoutModalVisible(false);
                    navigate(`/business/${businessId}/mpesa/setup`);
                  }}
                  className="w-full py-3.5 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl transition shadow-lg shadow-green-500/30"
                >
                  Setup M-Pesa Now
                </button>
                <button
                  onClick={() => setMpesaSetupRequired(false)}
                  className="w-full py-3.5 mt-3 bg-gray-100 dark:bg-[#2A2A2A] text-gray-700 dark:text-gray-300 font-bold rounded-xl hover:opacity-80 transition"
                >
                  Back to Checkout
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <button
                    onClick={() => setPaymentMethod('cash')}
                    className={`py-4 rounded-xl font-bold flex flex-col items-center gap-2 border-2 transition ${
                      paymentMethod === 'cash' 
                        ? 'border-primary bg-primary/10 text-primary' 
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#2A2A2A] text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <Banknote className="h-6 w-6" /> Cash
                  </button>
                  <button
                    onClick={() => setPaymentMethod('mpesa')}
                    className={`py-4 rounded-xl font-bold flex flex-col items-center gap-2 border-2 transition ${
                      paymentMethod === 'mpesa' 
                        ? 'border-green-500 bg-green-500/10 text-green-600' 
                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#2A2A2A] text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    <Phone className="h-6 w-6" /> M-Pesa
                  </button>
                </div>

                {paymentMethod === 'mpesa' && (
                  <div className="mb-6">
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">M-Pesa Number</label>
                    <input
                      type="tel"
                      placeholder="07... or 01..."
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    />
                    <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1 mt-4">Manual M-Pesa Code (Optional)</label>
                    <input
                      type="text"
                      placeholder="e.g. QWE123RTY"
                      value={mpesaManualCode}
                      onChange={(e) => setMpesaManualCode(e.target.value)}
                      className="w-full px-4 py-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary uppercase"
                    />
                  </div>
                )}

                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-1">Discount Amount (KES)</label>
                  <input
                    type="number"
                    placeholder="0"
                    value={discount}
                    onChange={(e) => setDiscount(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 dark:bg-[#2A2A2A] rounded-xl font-semibold text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>

                <div className="bg-gray-50 dark:bg-[#2A2A2A] rounded-xl p-4 mb-6">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-500 dark:text-gray-400 font-semibold">Subtotal</span>
                    <span className="text-gray-900 dark:text-white font-bold">KES {subtotal.toLocaleString()}</span>
                  </div>
                  {parseFloat(discount) > 0 && (
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-red-500 font-semibold">Discount</span>
                      <span className="text-red-500 font-bold">- KES {parseFloat(discount).toLocaleString()}</span>
                    </div>
                  )}
                  <div className="flex justify-between items-center pt-2 border-t border-gray-200 dark:border-gray-700 mt-2">
                    <span className="text-gray-900 dark:text-white font-black text-lg">Total</span>
                    <span className="text-primary dark:text-white font-black text-xl">
                      KES {Math.max(0, subtotal - (parseFloat(discount) || 0)).toLocaleString()}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleCheckoutSubmit}
                  disabled={processing}
                  className={`w-full py-4 text-white font-bold rounded-xl transition flex items-center justify-center gap-2 ${
                    paymentMethod === 'mpesa' ? 'bg-green-500 hover:bg-green-600 shadow-lg shadow-green-500/30' : 'bg-primary hover:bg-primary/90 shadow-lg shadow-primary/30'
                  } disabled:opacity-70`}
                >
                  {processing ? (
                    <>
                      <Loader2 className="h-5 w-5 animate-spin" />
                      <span>{paymentMethod === 'mpesa' ? 'Sending STK Push…' : 'Processing payment…'}</span>
                    </>
                  ) : (
                    <>
                      <CreditCard className="h-5 w-5" />
                      <span>{paymentMethod === 'mpesa' ? 'Send STK Push' : 'Confirm Payment'}</span>
                    </>
                  )}
                </button>
                {processing && (
                  <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-2 animate-pulse">
                    Connecting to server…
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}

      {/* Payment Status Modal */}
      {paymentStatusModalVisible && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-md">
          <div className="bg-white dark:bg-[#1E1E1E] w-full max-w-sm rounded-3xl p-8 shadow-2xl flex flex-col items-center text-center">
            
            {paymentStatus === 'checking' && (
              <>
                <div className="relative mb-6">
                  <div className="absolute inset-0 bg-green-500/20 rounded-full animate-ping"></div>
                  <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center relative z-10">
                    <Loader2 className="h-10 w-10 text-green-500 animate-spin" />
                  </div>
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Awaiting Payment</h3>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  Please ask the customer to enter their M-Pesa PIN on their phone.
                </p>
              </>
            )}

            {paymentStatus === 'success' && (
              <>
                <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle2 className="h-10 w-10 text-green-500" />
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Payment Successful!</h3>
                <p className="text-sm font-semibold text-gray-500 dark:text-gray-400">
                  The sale has been recorded successfully.
                </p>
              </>
            )}

            {paymentStatus === 'failed' && (
              <>
                <div className="w-20 h-20 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-6">
                  <XCircle className="h-10 w-10 text-red-500" />
                </div>
                <h3 className="text-xl font-black text-gray-900 dark:text-white mb-2">Payment Failed</h3>
                <p className="text-sm font-semibold text-red-500 mb-6 px-4">
                  {paymentErrorMessage}
                </p>
                <div className="flex gap-4 w-full">
                  <button
                    onClick={() => setPaymentStatusModalVisible(false)}
                    className="flex-1 py-3 bg-gray-100 dark:bg-[#2A2A2A] text-gray-900 dark:text-white font-bold rounded-xl hover:opacity-80 transition"
                  >
                    Close
                  </button>
                  <button
                    onClick={handleRetryPayment}
                    className="flex-1 py-3 bg-primary text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:opacity-90 transition"
                  >
                    <RefreshCw className="h-4 w-4" /> Retry
                  </button>
                </div>
              </>
            )}

          </div>
        </div>
      )}

        {/* Hardware Scanner Options Modal */}
        {scannerModalVisible && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
            <div className="bg-white dark:bg-[#1E1E1E] w-full max-w-md rounded-3xl p-6 shadow-2xl relative border border-light-border dark:border-dark-border overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-green-400" />
              <button 
                onClick={() => setScannerModalVisible(false)}
                className="absolute top-4 right-4 p-2 bg-gray-100 dark:bg-[#2A2A2A] rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400 transition"
              >
                <XCircle className="h-5 w-5" />
              </button>
              
              <div className="flex items-center gap-3 mb-6">
                <div className="p-3 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 rounded-xl">
                  <ScanBarcode className="h-6 w-6" />
                </div>
                <div>
                  <h2 className="text-xl font-black text-gray-900 dark:text-white">Hardware Scanners</h2>
                  <p className="text-xs text-green-600 dark:text-green-400 font-bold flex items-center gap-1 mt-0.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" /> System Ready
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-4 rounded-xl border-2 border-primary bg-primary/5 flex items-start gap-4">
                  <div className="p-2 bg-white dark:bg-[#2A2A2A] rounded-lg shadow-sm text-primary">
                    <Usb className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 dark:text-white flex items-center justify-between">
                      USB Scanner
                      <span className="text-[10px] uppercase font-black tracking-wider bg-primary text-white px-2 py-0.5 rounded-full">Active</span>
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      Simply plug in any standard USB barcode scanner. It will work automatically without any configuration.
                    </p>
                  </div>
                </div>

                <div className="p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-[#2A2A2A] flex items-start gap-4 opacity-90">
                  <div className="p-2 bg-white dark:bg-[#1E1E1E] border border-gray-100 dark:border-gray-800 rounded-lg shadow-sm text-blue-500">
                    <Bluetooth className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 dark:text-white flex items-center justify-between">
                      Bluetooth Scanner
                      <span className="text-[10px] uppercase font-black tracking-wider bg-green-500 text-white px-2 py-0.5 rounded-full">Supported</span>
                    </h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
                      Pair the scanner with this device via Bluetooth settings. It will emulate a keyboard seamlessly.
                    </p>
                  </div>
                </div>

                <button 
                  onClick={() => {
                    setScannerModalVisible(false);
                    toastCtx.info('Coming Soon', 'Device Camera scanning is currently being optimized for web.');
                  }}
                  className="w-full p-4 rounded-xl border border-gray-200 dark:border-gray-700 hover:border-primary/50 bg-white dark:bg-[#1E1E1E] flex items-center gap-4 transition group text-left"
                >
                  <div className="p-2 bg-gray-50 dark:bg-[#2A2A2A] rounded-lg text-gray-400 group-hover:text-primary transition">
                    <Camera className="h-5 w-5" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 dark:text-white group-hover:text-primary transition">Device Camera</h4>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Use your webcam or tablet camera</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </>
        )}
    </div>
  );
};
